<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useNIRApplicationsStore } from "@/domains/home/services/nir/registration/stores";
import FormDataViewer from "@/domains/home/services/nir/forms/components/FormDataViewer.vue";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";

const loading = ref(true);
const store = useNIRApplicationsStore()
const formStore = useFormsStore()
const institutionStore = useInstitutionStore()
const activeTab = ref<'form' | 'attachments'>('form')

const props = defineProps({
  id:String,
})

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  store.fetchDataUpdateTask(props.id || "")
      .then(() =>{
        loading.value = false
        formStore.fetchForm(store.dataUpdateTask?.formId || "")
        formStore.fetchFormFields(store.dataUpdateTask?.formId || "")
      })
})

</script>

<template>
  <div class="flex flex-col">
    <!-- Application Details - Always Visible -->
    <div class="flex flex-col bg-white rounded-lg shadow p-6 mb-4" id="details">
      <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
        <i class="fa fa-info-circle text-primary mr-2"></i>
        Application Details
      </h3>

      <div v-if="store.dataUpdateTask" class="grid grid-cols-2 gap-4">
        <!-- Tracking Number -->
        <div v-if="store.dataUpdateTask.trackingNumber" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tracking Number</label>
          <p class="text-sm text-gray-900 font-mono">{{ store.dataUpdateTask.trackingNumber }}</p>
        </div>
        <!-- Name -->
        <div v-if="store.dataUpdateTask.name" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Entity Name</label>
          <p class="text-sm text-gray-900 font-semibold">{{ store.dataUpdateTask.name }}</p>
        </div>

        <!-- Type -->
        <div v-if="store.dataUpdateTask.type" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</label>
          <p class="text-sm text-gray-900">{{ store.dataUpdateTask.type }}</p>
        </div>

        <!-- Sub Type -->
        <div v-if="store.dataUpdateTask.subType" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sub Type</label>
          <p class="text-sm text-gray-900">{{ store.dataUpdateTask.subType }}</p>
        </div>

        <!-- Form ID -->
        <div v-if="store.dataUpdateTask.formId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Form</label>
          <p class="text-sm text-gray-900 font-mono">{{ formStore.form?.name || '' }}</p>
        </div>

        <!-- Institution ID -->
        <div v-if="store.dataUpdateTask.institutionId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Institution</label>
          <p class="text-sm text-gray-900 font-mono" v-if="store.dataUpdateTask?.institutionId">{{ institutionStore.institutions?.find((i => i.id === store.dataUpdateTask?.institutionId))?.name || 'N/A' }}</p>
        </div>

        <!-- Status -->
        <div v-if="store.dataUpdateTask.status" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
          <span class="text-sm font-medium px-3 py-1 rounded-full w-fit"
                :class="{
                  'bg-gray-100 text-gray-600': store.dataUpdateTask.status === 'PENDING',
                  'bg-gray-100 text-gray-800': store.dataUpdateTask.status === 'SUBMITTED',
                  'bg-success-50 text-success-600 border border-success-200': store.dataUpdateTask.status === 'PREAPPROVAL',
                  'bg-primary-100 text-primary-800': store.dataUpdateTask.status === 'ASSIGNED',
                  'bg-success-100 text-success-800': store.dataUpdateTask.status === 'APPROVED',
                  'bg-danger-100 text-danger-800': store.dataUpdateTask.status === 'REJECTED',
                  'bg-warning-100 text-warning-800': store.dataUpdateTask.status === 'QUERIED',
                }">
            {{ store.dataUpdateTask.status }}
          </span>
        </div>

        <!-- Created At -->
        <div v-if="store.dataUpdateTask.createdAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Created At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.dataUpdateTask.createdAt).toLocaleString() }}</p>
        </div>

        <!-- Updated At -->
        <div v-if="store.dataUpdateTask.updatedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Updated At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.dataUpdateTask.updatedAt).toLocaleString() }}</p>
        </div>

        <!-- Approved At -->
        <div v-if="store.dataUpdateTask.approvedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Approved At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.dataUpdateTask.approvedAt).toLocaleString() }}</p>
        </div>

        <!-- Rejected At -->
        <div v-if="store.dataUpdateTask.rejectedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rejected At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.dataUpdateTask.rejectedAt).toLocaleString() }}</p>
        </div>

        <!-- Message -->
        <div v-if="store.dataUpdateTask.message" class="flex flex-col md:col-span-2">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
          <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">{{ store.dataUpdateTask.message }}</p>
        </div>
      </div>

      <div v-else class="text-center py-8 text-gray-500">
        <i class="fa fa-inbox text-4xl mb-2"></i>
        <p>No task details available</p>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="flex border-b border-gray-200 mb-4">
      <label
          @click="activeTab = 'form'"
          :class="[
            'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'form'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
      >
        <i class="fa fa-file-alt mr-2"></i>
        Form Data
      </label>
      <label
          @click="activeTab = 'attachments'"
          :class="[
            'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'attachments'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
      >
        <i class="fa fa-paperclip mr-2"></i>
        Attachments
        <span v-if="store.dataUpdateTask?.attachments?.length" class="ml-2 bg-primary-100 text-primary-800 text-xs font-medium px-2 py-0.5 rounded-full">
          {{ store.dataUpdateTask.attachments.length }}
        </span>
      </label>
    </div>

    <!-- Form Data Tab -->
    <div v-show="activeTab === 'form'">
      <FormDataViewer v-if="store.dataUpdateTask && formStore.formData" :form-data="formStore.formData" :value-data="store.dataUpdateTask.data"/>
    </div>

    <!-- Attachments Tab -->
    <div v-show="activeTab === 'attachments'">
      <div class="flex flex-col bg-white rounded-lg shadow p-6">
        <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
          <i class="fa fa-paperclip text-primary mr-2"></i>
          Application Attachments
        </h3>

        <div v-if="store.dataUpdateTask?.attachments?.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
              v-for="attachment in store.dataUpdateTask.attachments"
              :key="attachment.id"
              class="flex flex-col bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center">
                <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                  <i class="fa fa-file text-primary"></i>
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-gray-900">{{ attachment.title }}</h4>
                  <p class="text-xs text-gray-500 mt-0.5" v-if="attachment.description">{{ attachment.description }}</p>
                </div>
              </div>
            </div>

            <div class="mt-3 pt-3 border-t border-gray-200">
              <div class="flex flex-col space-y-1">
                <div class="flex flex-col text-xs text-gray-500">
                  <span class="font-bold">File ID</span>
                  <span class="font-mono truncate" :title="attachment.fileId">{{ attachment.fileId }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-12 text-gray-500">
          <i class="fa fa-folder-open text-4xl mb-3 text-gray-300"></i>
          <p class="text-gray-600">No attachments available for this application</p>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>